/**
 * Audit Templates Configuration Page
 *
 * Displays the list of CyberSweep audit templates with their
 * due date configuration settings. Compact styling to match
 * CyberSweep Operations pages.
 *
 * Data sourced exclusively from Gobodo/RDS Cyber_Sweep_Audit_Temp__stg5_c object.
 * Shows all records exactly as they appear in Salesforce.
 */

import React from 'react';
import { FileText, Clock, CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { useAuditTemplates, type GobodoAuditTemplate } from '../../services/queries/cyberSweepQueries';

interface AuditTemplatesPageProps {
  onBack?: () => void;
  onTemplateClick?: (templateId: string) => void;
}

/**
 * Standard template order for CyberSweep audits
 */
const TEMPLATE_ORDER = [
  'initial deletion',
  'initial audit',
  'final audit',
  'quarter #1 audit',
  'quarter #2 audit',
  'quarter #3 audit',
  'quarter #4 audit',
];

/**
 * Get sort order for a template name (lower = earlier in list)
 */
function getTemplateOrder(name: string): number {
  const lowerName = name.toLowerCase().trim();
  const index = TEMPLATE_ORDER.findIndex(t => lowerName.includes(t) || t.includes(lowerName));
  return index >= 0 ? index : 999; // Unknown templates go to the end
}

const AuditTemplatesPage: React.FC<AuditTemplatesPageProps> = ({ onBack, onTemplateClick }) => {
  const { data: gobodoTemplates, isLoading, error } = useAuditTemplates();

  // Show all records from Gobodo/Salesforce, sorted by workflow order
  const templates: GobodoAuditTemplate[] = [...(gobodoTemplates ?? [])].sort(
    (a, b) => getTemplateOrder(a.name) - getTemplateOrder(b.name)
  );

  return (
    <div className="space-y-3">
      {/* Header - Compact */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Audit Templates</h2>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">({templates.length} templates)</span>
          )}
        </div>
      </div>

      {/* Data Source Indicator - shown when no templates found */}
      {!isLoading && !error && templates.length === 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
          <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>No audit templates found in Gobodo. Templates will appear once synced from RDS.</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">
          <div className="flex items-start gap-2 text-xs text-red-700 dark:text-red-300">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>Error loading templates: {error.message}</span>
          </div>
        </div>
      )}

      {/* Info Card - Compact */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2">
        <div className="flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300">
          <Clock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">Due Date Calculation: </span>
            Phase 1 Due = Start Date + P1 Days. Phase 2 Due = P1 Due + P2 Days (if required).
          </div>
        </div>
      </div>

      {/* Templates Table - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Template Configuration
          </h3>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/30">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Template Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Phase 1 Due
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Phase 2 Due
                </th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  P2 Required
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Loading templates...</span>
                    </div>
                  </td>
                </tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No templates found
                  </td>
                </tr>
              ) : (
                templates.map((template) => (
                  <tr
                    key={template.id}
                    onClick={() => onTemplateClick?.(template.id)}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      onTemplateClick ? 'cursor-pointer' : ''
                    }`}
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {template.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        +{template.phase1DueDateDays} days
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {template.phase2Required ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                          +{template.phase2DueDateDays} days after P1
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          N/A
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-center">
                      {template.phase2Required ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-400 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditTemplatesPage;
